import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'

import type {
  ClipboardHandlers,
  ClipboardPayloadEntry,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type {
  GridClipboardPayload,
  ExtendedGridEventAppearance,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import {
  ShiftManagementAddRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDropDocument,
  ShiftsShiftFragmentDoc,
  ShiftsRestDayFragmentDoc,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'

interface UseClipboardProps {
  canUseClipboardActions: boolean
  shiftsRows: ShiftsRow[]
}

type ShiftPayload = Extract<GridClipboardPayload, { kind: 'shift' }>
type RestDayPayload = Extract<GridClipboardPayload, { kind: 'rest_day' }>
type ShiftGridEvent = Extract<ExtendedGridEventAppearance, { type: 'shift' }>
type RestDayGridEvent = Extract<ExtendedGridEventAppearance, { type: 'rest_day' }>

const isShiftPayload = (payload: GridClipboardPayload): payload is ShiftPayload =>
  payload.kind === 'shift'
const isRestDayPayload = (payload: GridClipboardPayload): payload is RestDayPayload =>
  payload.kind === 'rest_day'
const isShiftGridEvent = (event: ExtendedGridEventAppearance): event is ShiftGridEvent =>
  event.type === 'shift'
const isRestDayGridEvent = (event: ExtendedGridEventAppearance): event is RestDayGridEvent =>
  event.type === 'rest_day'

const payloadIdentityKey = (payload: GridClipboardPayload): string => {
  if (payload.kind === 'shift') return `shift:${payload.shift.id}`
  return `rest_day:${payload.restDay.id}`
}

const useGridClipboardActions = ({
  canUseClipboardActions,
  shiftsRows,
}: UseClipboardProps):
  | ClipboardHandlers<{ id: string }, ExtendedGridEventAppearance>
  | undefined => {
  const { company } = useCurrentSession()
  const [{ shiftsRows: currentShiftsRows }] = useShiftsRows()
  const { refreshStats } = useRefreshStats()
  const [bulkDropShifts] = useMutation(ShiftManagementBulkDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDropShiftmanagementShift,
    update: (cache, { data }, options) => {
      const droppedShifts = data?.shiftManagementMutations.bulkDropShiftmanagementShift.shifts
      if (!droppedShifts || droppedShifts.length === 0) return

      if (
        options?.variables?.dropAction === 'move' &&
        typeof options.context === 'object' &&
        options.context !== null &&
        'previousShifts' in options.context
      ) {
        const previousShifts = options?.context?.previousShifts || []

        const previousShiftsByEmployee = groupBy(previousShifts, 'employeeId')
        Object.entries(previousShiftsByEmployee).forEach(([employeeId, employeePreviousShifts]) => {
          const idsToRemove = new Set(employeePreviousShifts.map((s: { id: number }) => s.id))

          cache.modify({
            id: cache.identify({
              __typename: 'ShiftManagementEmployeesPermission',
              id: Number(employeeId),
            }),
            fields: {
              shifts(existingShifts = [], { readField }) {
                return existingShifts.filter((item: { _ref: string }) => {
                  const id = readField('id', item)
                  return typeof id !== 'number' || !idsToRemove.has(id)
                })
              },
            },
          })
        })
      }

      droppedShifts.forEach((droppedShift) => {
        cache.modify({
          id: cache.identify({
            __typename: 'ShiftManagementEmployeesPermission',
            id: droppedShift.employeeId,
          }),
          fields: {
            shifts(existingShifts = [], { readField }) {
              const existingIds = new Set<number>(
                existingShifts.map((item: { _ref: string }) => readField('id', item))
              )

              if (existingIds.has(droppedShift.id)) return existingShifts

              const newRef = cache.writeFragment({
                data: droppedShift,
                fragment: ShiftsShiftFragmentDoc,
                fragmentName: 'ShiftsShift',
              })

              return newRef ? [...existingShifts, newRef] : existingShifts
            },
          },
        })
      })
    },
  })
  const [bulkCreateRestDays] = useMutation(ShiftManagementAddRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementRestDay,
    update: (cache, { data }) => {
      const restDays = data?.shiftManagementMutations.bulkCreateShiftmanagementRestDay.restDays
      if (!restDays || restDays.length === 0) return

      const restDaysByEmployee = groupBy(restDays, (restDay) => restDay.employee?.id)
      Object.entries(restDaysByEmployee).forEach(([employeeId, employeeRestDays]) => {
        cache.modify({
          id: cache.identify({
            __typename: 'ShiftManagementEmployeesPermission',
            id: Number(employeeId),
          }),
          fields: {
            restDays(existingRestDays = [], { readField }) {
              const existingIds = new Set<number>(
                existingRestDays.map((item: { _ref: string }) => readField('id', item))
              )

              const newRefs = employeeRestDays
                .filter((s) => !existingIds.has(s.id))
                .map((restDay) =>
                  cache.writeFragment({
                    data: restDay,
                    fragment: ShiftsRestDayFragmentDoc,
                    fragmentName: 'ShiftsRestDay',
                  })
                )

              return [...existingRestDays, ...newRefs]
            },
          },
        })
      })
    },
  })
  const [bulkDeleteRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
    update: (cache, { data }) => {
      data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
        (restDay) => {
          cache.evict({
            id: cache.identify({
              __typename: 'ShiftManagementRestDay',
              id: restDay.id,
            }),
          })
        }
      )
    },
  })
  const [bulkDeleteShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
        cache.evict({
          id: cache.identify({
            __typename: 'ShiftManagementShift',
            id: shift.id,
          }),
        })
      })
    },
  })
  return useUnsafeMemo(() => {
    if (!canUseClipboardActions) return undefined

    const shiftsRowsByKey = new Map(shiftsRows.map((row) => [row.key, row]))
    const extractSourceData = (payload: GridClipboardPayload[]) => {
      const sourceShifts = payload.filter(isShiftPayload).map((item) => item.shift)
      const sourceRestDays = payload.filter(isRestDayPayload).map((item) => item.restDay)

      return {
        sourceShifts,
        sourceShiftIds: new Set(sourceShifts.map((shift) => shift.id)),
        sourceRestDays,
        sourceRestDayIds: new Set(sourceRestDays.map((restDay) => restDay.id)),
      }
    }
    const getTargetItemsToReplace = ({
      targetEvents,
      sourceShiftIds,
      sourceRestDayIds,
    }: {
      targetEvents: ExtendedGridEventAppearance[]
      sourceShiftIds: Set<number>
      sourceRestDayIds: Set<number>
    }) => {
      // Keep items already present in the source payload to avoid deleting the same
      // entity when source/target overlap (e.g. self-paste or move to overlapping cell).
      const targetShiftsToDelete = targetEvents
        .filter(isShiftGridEvent)
        .map((event) => event.shift)
        .filter((shift) => !sourceShiftIds.has(shift.id))

      const targetRestDaysToDelete = targetEvents
        .filter(isRestDayGridEvent)
        .map((event) => event.restDay)
        .filter((restDay) => !sourceRestDayIds.has(restDay.id))

      return { targetShiftsToDelete, targetRestDaysToDelete }
    }
    const deleteTargetItemsToReplace = async ({
      targetShiftsToDelete,
      targetRestDaysToDelete,
    }: {
      targetShiftsToDelete: Shift[]
      targetRestDaysToDelete: RestDay[]
    }) => {
      if (targetShiftsToDelete.length > 0) {
        const destroyTargetShiftsResult = await bulkDeleteShifts({
          variables: {
            ids: targetShiftsToDelete.map((shift) => shift.id),
            destroyBackupShifts: true,
            destroyAutomaticShifts: false,
          },
        })
        const destroyedTargetShiftsHandled = handleResult(
          destroyTargetShiftsResult,
          (_errors) => {}
        )
        if (!destroyedTargetShiftsHandled.success) return false
      }

      if (targetRestDaysToDelete.length > 0) {
        const destroyTargetRestDaysResult = await bulkDeleteRestDays({
          variables: { ids: targetRestDaysToDelete.map((restDay) => restDay.id) },
        })
        const destroyedTargetRestDaysHandled = handleResult(
          destroyTargetRestDaysResult,
          (_errors) => {}
        )
        if (!destroyedTargetRestDaysHandled.success) return false
      }

      return true
    }
    const pasteShiftsToTarget = async ({
      sourceShifts,
      targetRowId,
      targetDate,
      mode,
    }: {
      sourceShifts: Shift[]
      targetRowId: number
      targetDate: string
      mode: 'copy' | 'move'
    }) => {
      if (sourceShifts.length === 0) return true

      const result = await bulkDropShifts({
        variables: {
          shiftIds: sourceShifts.map((shift) => shift.id),
          employeeIds: [targetRowId],
          companyId: company.id,
          date: targetDate,
          dropAction: mode,
        },
        context: {
          previousShifts: sourceShifts,
        },
      })

      const handled = handleResult(result, (_errors) => {})
      if (!handled.success) return false

      const droppedShifts = handled.data.shifts
      if (!droppedShifts || droppedShifts.length === 0) return true

      droppedShifts.forEach((shift) => {
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      })

      return true
    }
    const pasteRestDaysToTarget = async ({
      sourceRestDays,
      targetRowId,
      targetDate,
      mode,
    }: {
      sourceRestDays: RestDay[]
      targetRowId: number
      targetDate: string
      mode: 'copy' | 'move'
    }) => {
      if (sourceRestDays.length === 0) return true

      const createResult = await bulkCreateRestDays({
        variables: {
          restDays: sourceRestDays.map(() => ({
            employeeId: targetRowId,
            date: targetDate,
            companyId: company.id,
          })),
        },
      })

      const createdHandled = handleResult(createResult, (_errors) => {})
      if (!createdHandled.success) return false

      createdHandled.data?.restDays?.forEach((restDay) => {
        RestDay.build({
          id: restDay.id,
          employee_id: restDay.employee?.id || 0,
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        })
      })

      if (mode === 'move') {
        const destroyResult = await bulkDeleteRestDays({
          variables: { ids: sourceRestDays.map((restDay) => restDay.id) },
        })
        const destroyedHandled = handleResult(destroyResult, (_errors) => {})
        if (!destroyedHandled.success) return false
      }

      return true
    }

    return {
      getCopyPayload: (context) => {
        const seenPayloads = new Set<string>()
        const payloadEntries = context.cells
          .map<ClipboardPayloadEntry>((selectionCell) => {
            const payload = selectionCell.context.events
              .flatMap((event) => (event.clipboardPayload ? [event.clipboardPayload] : []))
              .filter((clipboardPayload) => {
                const identity = payloadIdentityKey(clipboardPayload)
                if (seenPayloads.has(identity)) return false
                seenPayloads.add(identity)
                return true
              })

            return {
              rowOffset: selectionCell.rowOffset,
              columnOffset: selectionCell.columnOffset,
              payload,
            }
          })
          .filter((entry) => entry.payload.length > 0)

        if (payloadEntries.length === 0) return null

        return payloadEntries
      },
      canPaste: () => {
        // will be false if disabled cell or other conditions TBD
        return true
      },
      onPaste: async (context) => {
        const targetCellsByOffset = new Map(
          context.target.cells.map((selectionCell) => [
            `${selectionCell.rowOffset}:${selectionCell.columnOffset}`,
            selectionCell.context,
          ])
        )
        const payloadByTargetCell = new Map<
          string,
          {
            targetCell: (typeof context.target.cells)[number]['context']
            payload: GridClipboardPayload[]
          }
        >()

        context.payload.forEach((payloadEntry) => {
          const key = `${payloadEntry.rowOffset}:${payloadEntry.columnOffset}`
          const targetCell = targetCellsByOffset.get(key)
          if (!targetCell) return

          const current = payloadByTargetCell.get(key)
          if (!current) {
            payloadByTargetCell.set(key, { targetCell, payload: payloadEntry.payload })
            return
          }

          current.payload = [...current.payload, ...payloadEntry.payload]
        })

        for (const { targetCell, payload } of payloadByTargetCell.values()) {
          const targetRow = shiftsRowsByKey.get(targetCell.entity.id)
          if (!targetRow) continue

          const { sourceShifts, sourceRestDays, sourceShiftIds, sourceRestDayIds } =
            extractSourceData(payload)
          const { targetShiftsToDelete, targetRestDaysToDelete } = getTargetItemsToReplace({
            targetEvents: targetCell.events,
            sourceShiftIds,
            sourceRestDayIds,
          })
          const targetDate = i18nDateToString(targetCell.slot.start, 'backend')
          const targetCleared = await deleteTargetItemsToReplace({
            targetShiftsToDelete,
            targetRestDaysToDelete,
          })
          if (!targetCleared) return

          const shiftsPasted = await pasteShiftsToTarget({
            sourceShifts,
            targetRowId: targetRow.id,
            targetDate,
            mode: context.mode,
          })
          if (!shiftsPasted) return

          const restDaysPasted = await pasteRestDaysToTarget({
            sourceRestDays,
            targetRowId: targetRow.id,
            targetDate,
            mode: context.mode,
          })
          if (!restDaysPasted) return
        }

        if (payloadByTargetCell.size === 0) {
          return
        }

        refreshStats(currentShiftsRows)
      },
    }
  }, [
    canUseClipboardActions,
    company?.id,
    bulkDropShifts,
    bulkDeleteShifts,
    bulkCreateRestDays,
    bulkDeleteRestDays,
    shiftsRows,
    currentShiftsRows,
    refreshStats,
  ])
}

export { useGridClipboardActions }
